import type { LoaderProps as MantineLoaderProps } from "@mantine/core";
import { Loader as MantineLoader, Stack, Text, getSize } from "@mantine/core";
import type React from "react";
import { useEffect, useState } from "react";

const SIZES: Record<string, string> = {
  xs: "1rem",
  sm: "1.25rem",
  md: "1.5rem",
  lg: "2rem",
  xl: "3.5rem",
};

interface LoaderProps extends MantineLoaderProps {
  label?: string;
}

type CustomLoaderType = React.ComponentType<{ label?: string }> | undefined;

let CustomLoader: CustomLoaderType;

export const setCustomLoader = (component: CustomLoaderType) => {
  CustomLoader = component;
};

// A screen reader only announces a *change* to an already-registered live
// region -- if the region and its label both appear in the same DOM commit
// (the common case: a Loader that's already showing on its very first
// render), most screen readers won't announce anything, since there's no
// "before" state for them to have observed. Setting the label from a plain
// effect (rather than inline during render) defers it until after React's
// initial commit -- in a real browser that's after paint, giving assistive
// tech a chance to register the (empty) region first; deliberately not
// requestAnimationFrame, which -- unlike a plain effect -- React Testing
// Library's `act()` doesn't flush synchronously, so it would silently
// break any existing synchronous `getByText` assertion against this
// label. This keeps a single source of truth for the text (no second,
// hidden copy that would make such an assertion ambiguous instead).
function useDeferredLabel(label: string | undefined): string {
  const [deferred, setDeferred] = useState("");

  useEffect(() => {
    setDeferred(label ?? "");
  }, [label]);

  return deferred;
}

export const Loader = ({ size = "md", label, ...props }: LoaderProps) => {
  const deferredLabel = useDeferredLabel(label);

  if (CustomLoader) {
    return <CustomLoader label={label} />;
  }

  return label ? (
    <Stack
      justify="center"
      align="center"
      gap="sm"
      mt="xl"
      role="status"
      aria-live="polite"
    >
      <MantineLoader {...props} size={getSize(SIZES[size] ?? size)} />
      <Text c="text-secondary" size="xl">
        {deferredLabel}
      </Text>
    </Stack>
  ) : (
    <MantineLoader {...props} size={getSize(SIZES[size] ?? size)} />
  );
};
